import FormControlLabel from '@mui/material/FormControlLabel';
import React, { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled, SxProps, Theme } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { TreeViewProps } from '@mui/lab';

import Box from '../Box/Box';
import Input from '../Input/Input';
import Icon from '../Icon/Icon';
import Typography from '../Typography/Typography';

import Tree, { buildRecursiveTreeData, ROOT_ID, Leaf, TreeBranch } from './Tree';

type Props = {
  treeData: Leaf[];
  onSelectItems?: (ids: string[], isAllItemsToggled?: boolean) => void;
  title?: string;
  renderTitle?: () => JSX.Element;
  allEntityLabel?: string;
  isAllItems?: boolean;
  expandAll?: boolean;
  hasIndeterminateMode?: boolean;
  selected?: any[];
  shouldIncludeIndeterminate?: boolean;
  shouldExpandSelectedItemBranch?: boolean;
  showControls?: boolean;
  isReadOnly?: boolean;
  showSearch?: boolean;
  sx?: SxProps<Theme>;
} & TreeViewProps;

const BoxWrapperStyled = styled(Box)(({ theme }) => {
  return {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey.A700}`,
    padding: theme.spacing(2),
    minHeight: '100%',
    maxHeight: '100%',
    height: '100%'
  };
});

export const RecursiveTree = ({
  treeData,
  onSelectItems = () => {},
  expandAll = false,
  title,
  renderTitle,
  allEntityLabel,
  hasIndeterminateMode = false,
  selected = [],
  isAllItems = false,
  shouldIncludeIndeterminate = false,
  shouldExpandSelectedItemBranch = false,
  showControls = true,
  isReadOnly = false,
  showSearch = true,
  sx
}: Props) => {
  const [filterQuery, setFilterQuery] = useState('');
  const [isAllToggled, setIsAllToggled] = useState(isAllItems);
  const [data, setData] = useState({ id: ROOT_ID, name: null, children: [] as TreeBranch[] });
  const [allIds, setAllIds] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState(selected);

  useEffect(() => {
    const children = buildRecursiveTreeData(treeData);
    const allIds = treeData.map(({ id }) => id);
    setAllIds(allIds);
    setData({ id: ROOT_ID, name: null, children });
  }, [treeData]);

  const onSelectItemsCallback = (selectedItems: string[], isAllToggled: boolean) => {
    onSelectItems(selectedItems, isAllToggled);
  };

  return (
    <Box width="100%" height={`calc(100% - ${title ? 40 : 0}px)`} sx={sx}>
      {renderTitle ? (
        renderTitle()
      ) : (
        <Typography variant="body2" lineHeight="40px" color="text.primary">
          {title}
        </Typography>
      )}
      <BoxWrapperStyled>
        <Stack height="100%">
          <>
            {showControls && (
              <Box sx={{ padding: '6px 0' }}>
                <FormControl disabled={isReadOnly}>
                  <RadioGroup row name="items-tree-mode">
                    <FormControlLabel
                      sx={{ marginLeft: '5px' }}
                      labelPlacement="start"
                      value={`all_${title ?? 'entities'}`}
                      control={<Radio style={{ color: '#006aff' }} />}
                      checked={isAllToggled}
                      label={allEntityLabel ? allEntityLabel : null}
                      onChange={(_, value: boolean) => {
                        if (value) {
                          setIsAllToggled(true);
                          onSelectItemsCallback(allIds, true);
                        }
                      }}
                    />
                    <FormControlLabel
                      sx={{ marginLeft: '56px' }}
                      onChange={(_, value: boolean) => {
                        if (value) {
                          setIsAllToggled(false);
                          onSelectItemsCallback(selectedItems, false);
                        }
                      }}
                      checked={!isAllToggled}
                      labelPlacement="start"
                      value="custom"
                      control={<Radio style={{ color: '#006aff' }} />}
                      label="Custom"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
            {showSearch && (
              <Box sx={{ padding: '0 5px 10px 5px' }}>
                <Input
                  fullWidth={true}
                  disabled={isAllToggled}
                  placeholder="Search..."
                  value={filterQuery}
                  type="text"
                  onChange={event => {
                    setFilterQuery(event?.target?.value);
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <Icon name="search" />
                    </InputAdornment>
                  }
                />
              </Box>
            )}
          </>
          <Box overflow="auto">
            <Tree
              data={data}
              onChange={ids => {
                onSelectItemsCallback(ids, isAllToggled);
                setSelectedItems(ids);
              }}
              selectedIds={selected}
              hasIndeterminateMode={hasIndeterminateMode}
              filterQuery={filterQuery}
              defaultExpanded={expandAll ? allIds : [ROOT_ID, data.children[0]?.id]}
              isAllSelected={isAllToggled}
              shouldIncludeIndeterminate={shouldIncludeIndeterminate}
              shouldExpandSelectedItemBranch={shouldExpandSelectedItemBranch}
              isReadOnly={isReadOnly}
            />
          </Box>
        </Stack>
      </BoxWrapperStyled>
    </Box>
  );
};

export default RecursiveTree;
